'use client';

import { Box, HStack, Stack, VStack } from '@terra/ui-styled-system';
import { Button, LegacyDialog, Text } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';

export type UnpublishedConstituent = {
  id: string;
  title: string | null;
};

export type UnpublishedConstituentsViolation = {
  chainFindingId: string;
  chainTitle: string | null;
  unpublishedConstituents: UnpublishedConstituent[];
};

type UnpublishedConstituentsDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  violations: UnpublishedConstituentsViolation[];
  onNavigateToFinding?: (findingId: string) => void;
};

export function UnpublishedConstituentsDialog({
  isOpen,
  onOpenChange,
  violations,
  onNavigateToFinding,
}: UnpublishedConstituentsDialogProps) {
  const t = useTranslations(
    'designSystem.findings.editDrawer.sections.publicationStatus.unpublishedConstituents',
  );
  const tCommon = useTranslations('designSystem.common');

  const isBulk = violations.length > 1;

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => onOpenChange(open)}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxW="md" w="full">
          <Stack gap="4" p="5">
            <Stack gap="1">
              <LegacyDialog.Title>{t('title')}</LegacyDialog.Title>
              <LegacyDialog.Description>
                {isBulk ? t('descriptionBulk') : t('descriptionSingle')}
              </LegacyDialog.Description>
            </Stack>

            <VStack gap="3" alignItems="stretch">
              {violations.map((violation) => {
                const chainHeading = violation.chainTitle
                  ? t('chainHeading', { title: violation.chainTitle })
                  : t('chainHeadingFallback');

                return (
                  <Box
                    key={violation.chainFindingId}
                    p="3"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="border.default"
                    bg="bg.subtle"
                  >
                    <Text fontSize="sm" fontWeight="semibold" mb="2">
                      {chainHeading}
                    </Text>
                    <VStack gap="1.5" alignItems="stretch">
                      {violation.unpublishedConstituents.map((c) => (
                        <HStack
                          key={c.id}
                          gap="2"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Text fontSize="sm" truncate>
                            {c.title ?? t('untitledConstituent')}
                          </Text>
                          {onNavigateToFinding && (
                            <Button
                              variant="ghost"
                              size="xs"
                              onClick={() => onNavigateToFinding(c.id)}
                            >
                              {t('viewFinding')}
                            </Button>
                          )}
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                );
              })}
            </VStack>

            <HStack gap="3" justifyContent="flex-end" width="full">
              <Button size="sm" onClick={() => onOpenChange(false)}>
                {tCommon('close')}
              </Button>
            </HStack>
          </Stack>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
